import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { useHacker } from "@/hooks/useHackers";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";
import HireRequestModal from "@/components/feature/HireRequestModal";
import { featuredProjects } from "@/mocks/projects";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

function StarRating({ rating, size }: { rating: number | null; size?: string }) {
  const r = rating ?? 0;
  const fullStars = Math.floor(r);
  const hasHalf = r % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
  const cls = size === "lg" ? "text-sm" : "text-xs";

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <i key={`f-${i}`} className={`fas fa-star ${cls} text-yellow-400`} />
      ))}
      {hasHalf && <i className={`fas fa-star-half-alt ${cls} text-yellow-400`} />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <i key={`e-${i}`} className={`far fa-star ${cls} text-gray-300`} />
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="min-h-screen bg-[#161b22] animate-pulse">
      <div className="h-16 bg-gray-800" />
      <main className="pt-20 md:pt-24">
        <section className="bg-[#2c3e50] py-8 md:py-12">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#0d1117]/20" />
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-[#0d1117]/20 rounded w-48" />
                <div className="h-4 bg-[#0d1117]/20 rounded w-32" />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default function HackerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const hackerId = id ? parseInt(id, 10) : null;
  const { hacker, loading, error } = useHacker(hackerId);
  const { user } = useAuth();
  const [hireModalOpen, setHireModalOpen] = useState(false);

  const isOwnedByCurrentUser = user && hacker?.profile_id === user.id;

  // Fire GA4 secondary: expert_profile_view
  useEffect(() => {
    if (hacker) {
      trackEvent("expert_profile_view", {
        hacker_id: hacker.id,
        hacker_name: hacker.name,
        hacker_specialty: hacker.specialty,
        hacker_country: hacker.country || "",
      });
    }
  }, [hacker]);

  const handleHireClick = () => {
    trackEvent("hire_button_click", {
      hacker_id: hacker?.id || 0,
      hacker_name: hacker?.name || "",
      hacker_specialty: hacker?.specialty || "",
    });
    setHireModalOpen(true);
  };

  if (loading) {
    return <DetailSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <Header />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-6xl text-yellow-400 mb-4 block" />
            <h1 className="text-2xl font-light text-gray-200 mb-2">Something Went Wrong</h1>
            <p className="text-sm text-gray-500 mb-6">We couldn&apos;t load this hacker&apos;s profile. Please try again.</p>
            <Link to="/hackers" className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap">
              Browse All Hackers
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!hacker) {
    return (
      <div className="min-h-screen bg-[#0d1117]">
        <Header />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="fas fa-user-secret text-6xl text-gray-300 mb-4 block" />
            <h1 className="text-2xl font-light text-gray-200 mb-2">Hacker Not Found</h1>
            <p className="text-sm text-gray-500 mb-6">This hacker profile does not exist or has been removed.</p>
            <Link to="/hackers" className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap">
              Browse All Hackers
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const completedProjects = (hacker.rating ?? 0) >= 4.5 ? Math.floor(Math.random() * 200) + 200 : Math.floor(Math.random() * 150) + 50;

  const bioText = `${hacker.name} is a seasoned ${(hacker.specialty || "cybersecurity").toLowerCase()} specialist with ${hacker.experience || "extensive experience"} of hands-on experience. Based in ${hacker.country || "an undisclosed location"}, ${hacker.gender === "male" ? "he" : hacker.gender === "female" ? "she" : "they"} has built a reputation for delivering reliable results in ${hacker.skills && hacker.skills.length > 0 ? hacker.skills.join(", ") : (hacker.specialty || "cybersecurity").toLowerCase()}. Known for discretion and technical excellence, ${hacker.name} serves clients worldwide seeking elite hacking services.${hacker.age != null ? ` With a rating of ${hacker.rating ?? 0} out of 5 and ${hacker.age} years of age,` : ` With a rating of ${hacker.rating ?? 0} out of 5,`} ${hacker.gender === "male" ? "he" : hacker.gender === "female" ? "she" : "they"} combines technical prowess with real-world experience to deliver exceptional results for every engagement.`;

  const languages = [hacker.country === "Russia" ? "Russian" : hacker.country === "China" ? "Chinese" : hacker.country === "Japan" ? "Japanese" : hacker.country === "South Korea" ? "Korean" : hacker.country === "India" ? "Hindi" : "English", "English"].filter((v, i, a) => a.indexOf(v) === i);

  const hackerProjects = featuredProjects.slice(0, 4).map((p, i) => ({ ...p, id: p.id + i, author: hacker.name }));

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: hacker.name,
    url: `${siteUrl}/hacker/${hacker.id}`,
    image: hacker.avatar,
    description: `${hacker.name} is a ${hacker.specialty} specialist based in ${hacker.country} with ${hacker.experience} of experience.`,
    knowsAbout: hacker.skills || [hacker.specialty],
    nationality: hacker.country,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Hackers", item: `${siteUrl}/hackers` },
      { "@type": "ListItem", position: 3, name: hacker.name },
    ],
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <PageMeta
        title={`${hacker.name} — ${hacker.specialty} Specialist from ${hacker.country} | Hire on Hackers Lodge`}
        description={`Hire ${hacker.name}, a ${hacker.experience || "seasoned"} ${(hacker.specialty || "cybersecurity").toLowerCase()} specialist from ${hacker.country || "an undisclosed location"}. Rated ${hacker.rating ?? 0}/5 with ${completedProjects}+ completed projects. Expertise in ${hacker.skills?.join(", ") || (hacker.specialty || "cybersecurity")}.`}
        keywords={`${hacker.name}, ${hacker.specialty}, hire hacker, cybersecurity expert, ethical hacker, penetration testing, ${hacker.country} hacker`}
        canonicalUrl={`${siteUrl}/hacker/${hacker.id}`}
        ogType="profile"
        ogImage={hacker.avatar}
        schema={personSchema}
        breadcrumbSchema={breadcrumbSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto py-4">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Hackers", to: "/hackers" }, { label: hacker.name }]} />
        </div>
        {/* Profile Header */}
        <section className="bg-[#2c3e50] py-8 md:py-12">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <img
                src={hacker.avatar}
                alt={`${hacker.name} — ${hacker.specialty} specialist based in ${hacker.country}`}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover ring-4 ring-white/20 flex-shrink-0"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-xl md:text-2xl font-bold text-white">{hacker.name}</h1>
                  {hacker.verified && (
                    <i className="fas fa-check-circle text-[#5bc0de] text-lg" title="Verified Hacker" />
                  )}
                  {hacker.featured && (
                    <span className="bg-[#f0ad4e]/20 text-[#f0ad4e] px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide">Featured</span>
                  )}
                  {isOwnedByCurrentUser && (
                    <span className="bg-[#5cb85c]/20 text-[#5cb85c] px-2 py-0.5 rounded text-[10px] font-semibold uppercase">Your Profile</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="bg-[#5bc0de]/20 text-[#5bc0de] px-2.5 py-0.5 rounded text-xs font-semibold">{hacker.specialty}</span>
                  <span className="text-white/70"><i className="fas fa-map-marker-alt mr-1" />{hacker.country}</span>
                  <span className="text-white/70">{hacker.age != null ? `${hacker.age} yrs` : "—"} · {hacker.gender === "male" ? "Male" : hacker.gender === "female" ? "Female" : "—"}</span>
                  <StarRating rating={hacker.rating} size="lg" />
                  <span className="text-yellow-400 text-sm font-semibold">{(hacker.rating ?? 0).toFixed(1)}</span>
                </div>
              </div>
              <div className="flex flex-col items-center sm:items-end gap-2 flex-shrink-0">
                <span className="text-xl md:text-2xl font-bold text-white">{hacker.rate}</span>
                <span className="text-xs text-white/60">{completedProjects} projects completed</span>

                {isOwnedByCurrentUser && (
                  <Link
                    to="/dashboard"
                    className="bg-[#5bc0de] hover:bg-[#46b8da] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap"
                  >
                    <i className="fas fa-cog mr-2" />Manage Profile
                  </Link>
                )}

                {!isOwnedByCurrentUser && (
                  <button
                    onClick={handleHireClick}
                    className="bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-8 py-2.5 rounded transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="fas fa-envelope mr-2" />Hire {hacker.name.split(" ")[0]}
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                <h2 className="text-lg font-semibold text-gray-200 mb-3">About</h2>
                <p className="text-sm text-gray-400 leading-relaxed">{bioText}</p>
              </div>

              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                <h2 className="text-lg font-semibold text-gray-200 mb-4">Skills &amp; Expertise</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {hacker.certifications && hacker.certifications.length > 0 ? hacker.certifications.map((cert) => (
                        <span key={cert} className="text-xs bg-[#5bc0de]/10 text-[#5bc0de] px-2.5 py-1 rounded font-medium">{cert}</span>
                      )) : (
                        <span className="text-xs bg-[#5bc0de]/10 text-[#5bc0de] px-2.5 py-1 rounded font-medium">CEH</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Languages</h3>
                    <div className="flex flex-wrap gap-2">
                      {languages.map((lang) => (
                        <span key={lang} className="text-xs bg-[#1c2128] text-gray-400 px-2.5 py-1 rounded font-medium">{lang}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-gray-800">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Specialty Areas</h3>
                  <div className="flex flex-wrap gap-2">
                    {hacker.skills && hacker.skills.length > 0 ? hacker.skills.map((s) => (
                      <span key={s} className="text-xs bg-[#5cb85c]/10 text-[#5cb85c] px-2.5 py-1 rounded font-medium">{s}</span>
                    )) : (
                      <>
                        <span className="text-xs bg-[#5cb85c]/10 text-[#5cb85c] px-2.5 py-1 rounded font-medium">{hacker.specialty}</span>
                        <span className="text-xs bg-[#5cb85c]/10 text-[#5cb85c] px-2.5 py-1 rounded font-medium">Network Security</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {hacker.prizes && hacker.prizes.length > 0 && (
                <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                  <h2 className="text-lg font-semibold text-gray-200 mb-4">Prizes &amp; Awards</h2>
                  <ul className="space-y-2">
                    {hacker.prizes.map((prize, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                        <i className="fas fa-trophy text-[#f0ad4e] mt-0.5 flex-shrink-0" />
                        <span>{prize}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                <h2 className="text-lg font-semibold text-gray-200 mb-4">Recent Projects</h2>
                <ul className="divide-y divide-gray-100">
                  {hackerProjects.map((proj) => (
                    <li key={proj.id} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={proj.authorAvatar} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                        <Link to={`/project/${proj.id}`} className="text-sm text-gray-200 hover:text-[#5bc0de] truncate font-medium">{proj.title}</Link>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                        <span className="text-xs text-gray-500 hidden sm:inline">{proj.date}</span>
                        <span className="text-sm font-semibold text-gray-300">{proj.budget}</span>
                        <Link to={`/project/${proj.id}`} className="text-xs text-[#5bc0de] hover:text-[#46b8da] font-semibold whitespace-nowrap">
                          View <i className="fas fa-arrow-right ml-1" />
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#0d1117] rounded border border-gray-700 p-5 md:p-6">
                <h2 className="text-lg font-semibold text-gray-200 mb-4">
                  Client Reviews
                  <span className="text-sm font-normal text-gray-400 ml-2">(0)</span>
                </h2>
                <p className="text-sm text-gray-500 italic">No reviews yet. Be the first to hire {hacker.name.split(" ")[0]}!</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              <div className="bg-[#0d1117] rounded border border-gray-700 p-5">
                <h3 className="text-sm font-semibold text-gray-200 mb-4">Profile Stats</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Experience</span>
                    <span className="text-sm font-semibold text-gray-300">{hacker.experience}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Projects Done</span>
                    <span className="text-sm font-semibold text-gray-300">{completedProjects}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Hourly Rate</span>
                    <span className="text-sm font-semibold text-[#5cb85c]">{hacker.rate}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Rating</span>
                    <div className="flex items-center gap-2">
                      <StarRating rating={hacker.rating} />
                      <span className="text-sm font-semibold text-gray-300">{(hacker.rating ?? 0).toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#5cb85c] rounded p-5 text-white">
                <h3 className="text-base font-semibold mb-2">Ready to Work Together?</h3>
                <p className="text-xs text-white/80 mb-4 leading-relaxed">
                  Submit a request — our concierge team will review your needs and match you with the right hacker.
                </p>
                <button
                  onClick={handleHireClick}
                  className="w-full bg-[#0d1117] text-[#5cb85c] hover:bg-[#1c2128] text-sm font-semibold px-4 py-2.5 rounded transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="fas fa-paper-plane mr-2" />Send Request
                </button>
              </div>

              <Link
                to="/hackers"
                className="flex items-center gap-2 text-sm text-red-500 hover:text-red-400 transition-colors"
              >
                <i className="fas fa-arrow-left" /> Back to Hackers Directory
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <HireRequestModal
        isOpen={hireModalOpen}
        onClose={() => setHireModalOpen(false)}
        hacker={hacker}
      />
    </div>
  );
}